/**
 * STRATUM ID Normalizer & Entity Resolution Service
 * 
 * Centralized, deterministic resolution of Project and Task IDs across:
 * - Canonical representations ('inbox_inforum', 'comm_internal')
 * - User/Tenant-scoped Cloud IDs ('inbox_inforum_<uid>', 'comm_internal_<uid>')
 * - Legacy IDs ('__comm__')
 * - Composite Task IDs ('<projectId>_<taskId>') vs Raw Task IDs ('<taskId>')
 * 
 * Guarantees 100% backward compatibility with existing Firestore datasets,
 * local storage caches, and URL routing.
 */

export const CANONICAL_INBOX_ID = 'inbox_inforum';
export const CANONICAL_COMM_ID = 'comm_internal';

/**
 * Checks whether the given project identifier corresponds to the permanent Inbox project.
 * Handles canonical, user-scoped (e.g. inbox_inforum_<uid>), tenant-scoped, and casing variations.
 */
export function isInboxProject(projectId?: string | null): boolean {
  if (!projectId) return false;
  const normalized = String(projectId).trim().toLowerCase();
  return (
    normalized === CANONICAL_INBOX_ID ||
    normalized.startsWith(`${CANONICAL_INBOX_ID}_`) ||
    normalized === 'inbox' ||
    normalized === 'posteingang' ||
    normalized.startsWith('posteingang_')
  );
}

/**
 * Checks whether the given project identifier corresponds to the permanent Communication/CRM project.
 * Handles canonical, user-scoped (e.g. comm_internal_<uid>), tenant-scoped, legacy '__comm__', and casing variations.
 */
export function isCommProject(projectId?: string | null): boolean {
  if (!projectId) return false;
  const normalized = String(projectId).trim().toLowerCase();
  return (
    normalized === CANONICAL_COMM_ID ||
    normalized.startsWith(`${CANONICAL_COMM_ID}_`) ||
    normalized === '__comm__' ||
    normalized === 'comm' ||
    normalized === 'communication' ||
    normalized === 'kommunikation' ||
    normalized.startsWith('kommunikation_')
  );
}

const VIRTUAL_VIEW_PROJECT_IDS = new Set([
  'global',
  'globalkanban',
  'global_kanban',
  'default',
  'dashboard',
  'metricsdashboard',
  'impacteffort',
  'nodatetasks',
  'globalcalendar',
  'aiassistant',
  'analysis',
  'projectpool',
  'globalgantt',
]);

const SYSTEM_PROJECT_NAMES = new Set([
  'nicht zugeordnet',
  'posteingang',
  'inbox',
  'kommunikation',
  'communication',
]);

/**
 * Checks whether a project ID (and optional name) represents a system container
 * (Inbox / "Nicht zugeordnet", Communication / "Kommunikation", or virtual global route)
 * rather than a real user-selectable project.
 */
export function isSystemProject(projectId?: string | null, projectName?: string | null): boolean {
  if (!projectId) return true;
  if (isInboxProject(projectId) || isCommProject(projectId)) return true;
  const normId = String(projectId).trim().toLowerCase();
  if (VIRTUAL_VIEW_PROJECT_IDS.has(normId)) return true;
  if (projectName) {
    const normName = String(projectName).trim().toLowerCase();
    if (SYSTEM_PROJECT_NAMES.has(normName)) return true;
  }
  return false;
}

/**
 * Filters a project list down to real user projects (excluding `comm_internal`, `inbox_inforum` / "Nicht zugeordnet",
 * and virtual views) and sorts them in the exact same order as the project sidebar.
 */
export function filterAndSortRealProjects<
  T extends {
    id: string;
    name?: string;
    archived?: boolean;
    workspace?: string;
    order?: number;
    lastModified?: number;
  }
>(
  projects: T[],
  options?: {
    workspace?: string;
    activeWorkspace?: string;
    includeArchived?: boolean;
    onlyArchived?: boolean;
  }
): T[] {
  const { workspace, activeWorkspace, includeArchived = false, onlyArchived = false } = options || {};
  const targetWorkspace = workspace ?? activeWorkspace;
  return (projects || [])
    .filter(p => {
      if (!p || !p.id) return false;
      if (isSystemProject(p.id, p.name)) return false;
      if (onlyArchived) {
        if (!p.archived) return false;
      } else if (!includeArchived && p.archived) {
        return false;
      }
      if (targetWorkspace && (p.workspace || 'professional') !== targetWorkspace) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (a.order !== undefined || b.order !== undefined) {
        const orderA = a.order ?? 9999;
        const orderB = b.order ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
      }
      return (b.lastModified || 0) - (a.lastModified || 0);
    });
}

/**
 * Resolves any project identifier to its canonical representation.
 * - Any inbox-related ID -> 'inbox_inforum'
 * - Any communication/crm-related ID -> 'comm_internal'
 * - Any other standard project -> trimmed ID as-is
 */
export function canonicalProjectId(projectId?: string | null): string {
  if (!projectId) return '';
  const trimmed = String(projectId).trim();
  if (isInboxProject(trimmed)) return CANONICAL_INBOX_ID;
  if (isCommProject(trimmed)) return CANONICAL_COMM_ID;
  return trimmed;
}

/**
 * Determines whether two project identifiers point to the same logical project.
 * Accounts for canonical matching, scoped IDs, and direct string equality.
 */
export function areProjectIdsMatching(idA?: string | null, idB?: string | null): boolean {
  if (!idA && !idB) return true;
  if (!idA || !idB) return false;

  const strA = String(idA).trim();
  const strB = String(idB).trim();

  if (strA === strB) return true;

  const canonA = canonicalProjectId(strA);
  const canonB = canonicalProjectId(strB);

  // If either is inbox or communication, compare canonical forms
  if (isInboxProject(strA) || isInboxProject(strB)) {
    return canonA === canonB;
  }
  if (isCommProject(strA) || isCommProject(strB)) {
    return canonA === canonB;
  }

  return canonA === canonB;
}

/**
 * Safely extracts the raw task ID from a composite or prefixed string.
 * Example:
 * - 'inbox_inforum_17917365374238q4' -> '17917365374238q4'
 * - 'my_project_12345' with knownProjectId 'my_project' -> '12345'
 * - '12345' -> '12345'
 */
export function extractRawTaskId(id: string | number, knownProjectId?: string | null): string {
  const strId = String(id).trim();
  if (!strId) return '';

  if (knownProjectId) {
    const rawProj = String(knownProjectId).trim();
    const canonProj = canonicalProjectId(rawProj);

    if (strId.startsWith(`${rawProj}_`)) {
      return strId.slice(rawProj.length + 1);
    }
    if (canonProj && strId.startsWith(`${canonProj}_`)) {
      return strId.slice(canonProj.length + 1);
    }
  }

  // Handle default inbox and comm prefixes
  if (strId.startsWith(`${CANONICAL_INBOX_ID}_`)) {
    return strId.slice(CANONICAL_INBOX_ID.length + 1);
  }
  if (strId.startsWith(`${CANONICAL_COMM_ID}_`)) {
    return strId.slice(CANONICAL_COMM_ID.length + 1);
  }

  return strId;
}

/**
 * Checks whether a target task ID matches a candidate task ID.
 * Handles:
 * - Exact string match ('123' === '123')
 * - Composite prefix ('inbox_inforum_123' matches '123')
 * - Reverse composite prefix ('123' matches 'inbox_inforum_123')
 * - Project-scoped prefixing with known candidate project ID
 */
export function isMatchingTaskId(
  targetId: string | number,
  candidateTaskId: string | number,
  candidateProjectId?: string | null
): boolean {
  if (targetId === undefined || targetId === null || candidateTaskId === undefined || candidateTaskId === null) {
    return false;
  }

  const targetStr = String(targetId).trim();
  const candStr = String(candidateTaskId).trim();

  // 1. Direct equality
  if (targetStr === candStr) return true;

  // 2. Extracted raw IDs match
  const rawTarget = extractRawTaskId(targetStr, candidateProjectId);
  const rawCand = extractRawTaskId(candStr, candidateProjectId);
  if (rawTarget && rawCand && rawTarget === rawCand) return true;

  // 3. Project scoped composite check
  if (candidateProjectId) {
    const projStr = String(candidateProjectId).trim();
    const canonProj = canonicalProjectId(projStr);

    if (targetStr === `${projStr}_${candStr}` || targetStr === `${canonProj}_${candStr}`) {
      return true;
    }
    if (candStr === `${projStr}_${targetStr}` || candStr === `${canonProj}_${targetStr}`) {
      return true;
    }
  }

  // 4. Safe suffix check (only if separated by an underscore to avoid false substring collisions)
  if (targetStr.endsWith(`_${candStr}`) || candStr.endsWith(`_${targetStr}`)) {
    return true;
  }

  return false;
}

/**
 * Generates a clean, consistent composite ID for UI routing or drag-and-drop handles.
 */
export function buildCompositeTaskId(projectId: string, taskId: string | number): string {
  const cleanProj = canonicalProjectId(projectId);
  const rawTask = extractRawTaskId(taskId, projectId);
  return `${cleanProj}_${rawTask}`;
}

/**
 * Safely extracts a task ID from a Drag-and-Drop identifier (e.g. 'v::p-1_task_abc' or 'p-1_task_abc')
 * by stripping known DND prefixes and known project prefixes (longest match first),
 * preserving underscores inside the task ID itself (L-2).
 */
export function extractTaskIdFromDndId(
  dndId: string | number | null | undefined,
  knownProjectIds?: string[],
  fallbackProjectId?: string | null
): string {
  if (dndId === null || dndId === undefined) return '';
  const stripped = String(dndId).trim().replace(/^(p|pa|v|s|person)::/, '');
  if (!stripped) return '';

  if (Array.isArray(knownProjectIds) && knownProjectIds.length > 0) {
    const sortedProjIds = [...knownProjectIds]
      .filter(Boolean)
      .map(id => String(id).trim())
      .sort((a, b) => b.length - a.length);

    for (const projId of sortedProjIds) {
      if (stripped.startsWith(`${projId}_`)) {
        return stripped.slice(projId.length + 1);
      }
      const canonProj = canonicalProjectId(projId);
      if (canonProj && stripped.startsWith(`${canonProj}_`)) {
        return stripped.slice(canonProj.length + 1);
      }
    }
  }

  return extractRawTaskId(stripped, fallbackProjectId);
}

/**
 * Resolves a task across multiple projects with strict project scoping (H-4):
 * 1. Direct `activeDataTask` from the drag event if present
 * 2. Exact composite (`${projectId}_${taskId}`) or `globalId` match
 * 3. Match within `preferredProjectId` (e.g. active/target project) before scanning other projects
 * 4. Longest known project prefix match (`${project.id}_`)
 * 5. Exact raw task ID match across projects
 */
export function resolveScopedTaskById<
  T extends { id: string | number; projectId?: string; globalId?: string }
>(
  rawId: string | number | null | undefined,
  options: {
    activeDataTask?: T | null;
    projects?: { id: string; tasks?: T[] }[];
    tasksFallback?: T[];
    preferredProjectId?: string | null;
  }
): T | null {
  const { activeDataTask, projects, tasksFallback, preferredProjectId } = options;
  if (activeDataTask) return activeDataTask;
  if (rawId === null || rawId === undefined) return null;

  const strId = String(rawId).trim();
  const cleanId = strId.replace(/^(p|pa|v|s|person)::/, '');
  if (!cleanId) return null;

  if (Array.isArray(projects) && projects.length > 0) {
    const withProject = (t: T, projectId: string): T =>
      t.projectId ? t : ({ ...t, projectId } as T);

    // Pass 1: Exact composite ID (`${projectId}_${taskId}`) or `globalId` match
    for (const p of projects) {
      for (const t of p.tasks || []) {
        const effProjId = t.projectId || p.id;
        const canonProjId = canonicalProjectId(effProjId);
        if (
          (t.globalId && (t.globalId === strId || t.globalId === cleanId)) ||
          `${effProjId}_${t.id}` === cleanId ||
          `${p.id}_${t.id}` === cleanId ||
          (canonProjId && `${canonProjId}_${t.id}` === cleanId)
        ) {
          return withProject(t, effProjId);
        }
      }
    }

    // Pass 2: Preferred project scope (prevents cross-project ID collision when reordering inside a project)
    if (preferredProjectId) {
      const prefProj = projects.find(p => areProjectIdsMatching(p.id, preferredProjectId));
      if (prefProj) {
        const rawInPref = extractRawTaskId(cleanId, prefProj.id);
        const foundInPref = (prefProj.tasks || []).find(
          t => String(t.id) === cleanId || String(t.id) === rawInPref
        );
        if (foundInPref) {
          return withProject(foundInPref, foundInPref.projectId || prefProj.id);
        }
      }
    }

    // Pass 3: Known project prefix match (longest project ID first)
    const projectsByIdLength = [...projects].sort((a, b) => b.id.length - a.id.length);
    for (const p of projectsByIdLength) {
      if (cleanId.startsWith(`${p.id}_`)) {
        const remainder = cleanId.slice(p.id.length + 1);
        const matched = (p.tasks || []).find(t => String(t.id) === remainder);
        if (matched) return withProject(matched, matched.projectId || p.id);
      }
    }

    // Pass 4: Exact raw task ID match across projects
    for (const p of projects) {
      const matched = (p.tasks || []).find(t => String(t.id) === cleanId);
      if (matched) return withProject(matched, matched.projectId || p.id);
    }
  }

  if (Array.isArray(tasksFallback) && tasksFallback.length > 0) {
    // 1. Exact composite / globalId match
    const exactComposite = tasksFallback.find(
      t =>
        (t.globalId && (t.globalId === strId || t.globalId === cleanId)) ||
        (t.projectId && `${t.projectId}_${t.id}` === cleanId) ||
        (t.projectId && `${canonicalProjectId(t.projectId)}_${t.id}` === cleanId)
    );
    if (exactComposite) return exactComposite;

    // 2. Preferred project match
    if (preferredProjectId) {
      const inPref = tasksFallback.find(
        t =>
          t.projectId &&
          areProjectIdsMatching(t.projectId, preferredProjectId) &&
          (String(t.id) === cleanId ||
            String(t.id) === extractRawTaskId(cleanId, preferredProjectId))
      );
      if (inPref) return inPref;
    }

    // 3. Exact task ID match
    const exactId = tasksFallback.find(t => String(t.id) === cleanId || String(t.id) === strId);
    if (exactId) return exactId;
  }

  return null;
}
